import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Play, Clock, Bell, X, Youtube } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHANNEL_URL = "https://www.youtube.com/@BUBZPLUSSTREAMINGSERVICE";

const FALLBACK = [
  { id: "f1", number: 1, title: "Zoo Adventures", synopsis: "Bubz and Bubble float past the lion gates for a day of zoo-sized surprises.", duration: "2 min", status: "out_now", video_id: "iqKuhE2cBqE", color: "pink" },
  { id: "f2", number: 2, title: "What Lies Under The Bed", synopsis: "Bubz is sure something bubbly is hiding under the bed — time to investigate!", duration: "2 min", status: "out_now", video_id: "e8FrPNCGFC4", color: "sky" },
  { id: "f3", number: 3, title: "Windy Misadventure", synopsis: "A gusty day sweeps Bubble away, and Bubz must chase him across the park.", duration: "2 min", status: "out_now", video_id: "tYwUjGhBTxk", color: "sun" },
  { id: "f4", number: 4, title: "Bubble's Big Day", synopsis: "It's Bubble's birthday, and Bubz has the bubbliest surprise planned in the whole wide park.", duration: "2 min", status: "coming_soon", video_id: null, color: "grape" },
];

const COLOR_MAP = {
  pink: { bg: "bg-bubz-pink/15", badge: "bg-bubz-pink", ring: "border-bubz-pink/40" },
  sky: { bg: "bg-bubz-sky/15", badge: "bg-bubz-sky", ring: "border-bubz-sky/40" },
  sun: { bg: "bg-bubz-sun/20", badge: "bg-bubz-sun", ring: "border-bubz-sun/50" },
  grape: { bg: "bg-bubz-grape/15", badge: "bg-bubz-grape", ring: "border-bubz-grape/40" },
  grass: { bg: "bg-bubz-grass/15", badge: "bg-bubz-grass", ring: "border-bubz-grass/40" },
};

function EpisodeCard({ ep, index, onPlay }) {
  const c = COLOR_MAP[ep.color] || COLOR_MAP.sky;
  const playable = Boolean(ep.video_id);
  return (
    <Reveal delay={index * 0.08}>
      <motion.article
        data-testid={`episode-card-${ep.number}`}
        className={`group flex h-full flex-col overflow-hidden rounded-[2rem] border-2 ${c.ring} bg-white shadow-[0_14px_40px_rgba(112,193,255,0.18)]`}
        whileHover={{ y: -8, rotate: index % 2 === 0 ? -0.7 : 0.7 }}
        transition={{ type: "spring", stiffness: 220, damping: 18 }}
      >
        <div className={`relative flex aspect-[16/10] items-center justify-center overflow-hidden ${c.bg}`}>
          {playable && (
            <img
              src={`https://i.ytimg.com/vi/${ep.video_id}/hqdefault.jpg`}
              alt={`${ep.title} episode thumbnail`}
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
          )}
          <span className="absolute left-4 top-4 rounded-full bg-bubz-ink px-3 py-1 font-display text-xs font-semibold uppercase tracking-wider text-white">
            Ep {ep.number}
          </span>
          <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-white/85 px-3 py-1 text-xs font-extrabold text-bubz-ink">
            <Clock size={13} /> {ep.duration}
          </span>
          {!playable && (
            <>
              <span className="bubble h-16 w-16 rounded-full opacity-70" />
              <span className="bubble absolute bottom-6 right-8 h-8 w-8 rounded-full opacity-60" />
              <span className="bubble absolute bottom-10 left-10 h-5 w-5 rounded-full opacity-60" />
            </>
          )}
          {playable && (
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-bubz-pink/95 shadow-[0_8px_24px_rgba(255,117,181,0.6)] transition-transform duration-200 group-hover:scale-110">
              <Play size={24} className="ml-1 text-white" fill="currentColor" />
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col p-6">
          <span
            data-testid={`episode-status-${ep.number}`}
            className={`mb-3 inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest text-white ${ep.status === "out_now" ? "bg-bubz-grass" : c.badge}`}
          >
            {ep.status === "out_now" ? (
              <><Play size={11} fill="currentColor" /> Out now</>
            ) : ep.status === "drops_this_week" ? (
              <><Bell size={11} /> Drops this week</>
            ) : (
              "Coming soon"
            )}
          </span>
          <h3 className="font-display text-xl font-semibold text-bubz-ink sm:text-2xl">{ep.title}</h3>
          <p className="mt-2 flex-1 text-sm font-semibold leading-relaxed text-bubz-ink/60">{ep.synopsis}</p>

          {playable ? (
            <motion.button
              data-testid={`episode-play-button-${ep.number}`}
              onClick={() => onPlay(ep)}
              className="mt-5 flex items-center justify-center gap-2 rounded-full bg-bubz-ink px-5 py-3 font-display text-base font-semibold text-white"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.95 }}
            >
              <Play size={16} fill="currentColor" /> Play episode
            </motion.button>
          ) : (
            <motion.a
              data-testid={`episode-subscribe-button-${ep.number}`}
              href={CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex items-center justify-center gap-2 rounded-full border-[3px] border-bubz-ink/15 bg-bubz-cream px-5 py-3 font-display text-base font-semibold text-bubz-ink"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.95 }}
            >
              <Youtube size={18} className="text-[#FF0000]" /> Subscribe to watch first
            </motion.a>
          )}
        </div>
      </motion.article>
    </Reveal>
  );
}

export default function Episodes() {
  const [episodes, setEpisodes] = useState(FALLBACK);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/episodes`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setEpisodes(r.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section id="episodes" data-testid="episodes-section" className="relative py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.3em] text-bubz-pink">Watch the show</p>
          <h2 className="max-w-xl font-display text-3xl font-bold leading-tight text-bubz-ink sm:text-4xl lg:text-5xl">
            A bubbly new adventure, <span className="text-bubz-sky">every single week</span>
          </h2>
          <p className="mt-4 max-w-lg text-base font-semibold text-bubz-ink/60 sm:text-lg">
            Two-minute episodes made for little dreamers — dropping weekly on our YouTube channel.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {episodes.map((ep, i) => (
            <EpisodeCard key={ep.id} ep={ep} index={i} onPlay={setActive} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="episode-player-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-bubz-ink/70 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
          >
            <motion.div
              className="w-full max-w-3xl overflow-hidden rounded-[2rem] bg-black shadow-2xl"
              initial={{ scale: 0.85, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.85, y: 40 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-bubz-ink px-5 py-3">
                <span className="font-display text-sm font-semibold text-white">
                  Ep {active.number} — {active.title}
                </span>
                <button
                  data-testid="episode-player-close"
                  aria-label="Close player"
                  onClick={() => setActive(null)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="aspect-video">
                <iframe
                  title={`BUBZ & BUBBLES — ${active.title}`}
                  src={`https://www.youtube.com/embed/${active.video_id}?rel=0`}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
